import {
  PrimaryButton,
  Section,
  SectionContent,
  SectionDescription,
  SectionTitle,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getAccountUsablePaymentMethod,
  getCurrentQuote,
  PaymentMethodType,
} from '../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../hooks/auth'
import { QuotePremiums, useQuotePremiums } from '../../../../../hooks/premiums'
import {
  useStripePaymentAdmin,
  useStripePaymentUser,
} from '../../../../../hooks/stripePayment'
import {
  TKey,
  tPaymentMethods,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { PaymentMethod, PaymentScheduleConfig } from '../../../../../state/configuration'
import { usePrincipal } from '../../../../authentication'
import { useCustomerAccount, usePaymentMethods } from '../../../policy/billing'
import { useProduct } from '../../../productContext'
import { useProposal } from '../../ProposalProvider'
import { PaymentPath } from '../PaymentPath'
import { DirectDebitSetup } from './DirectDebitSetup'

export interface PaymentDualProps {
  paymentMethod: PaymentMethod
  config: PaymentScheduleConfig
}

const tKey = TKey('components.payment-dual')

export const PaymentDual: FunctionComponent<PaymentDualProps> = ({
  config,
  paymentMethod,
}) => {
  const { t } = useWriskTranslation()

  const { apiClient, isAdmin } = usePrincipal()

  const { proposal, dispatch, refresh } = useProposal()
  const { product } = useProduct()
  const { paymentMethods } = usePaymentMethods()
  const { customerAccount } = useCustomerAccount()

  const quote = getCurrentQuote(proposal)

  const usablePaymentMethod = getAccountUsablePaymentMethod(
    paymentMethods,
    PaymentMethodType.BACS_DEBIT,
    customerAccount.accountCode,
  )

  const premiums = useQuotePremiums(quote, product)

  const paymentType = getPaymentType(premiums)

  const onStripeUserPayment = useStripePaymentUser({
    saveCard: false,
    proposalCode: proposal.proposalCode,
    successPath: [location.pathname, PaymentPath.PAYMENT_CALLBACK].join('/'),
    cancelPath: location.pathname,
  })

  const onStripeAdminPayment = useStripePaymentAdmin({
    saveCard: false,
    proposalCode: proposal.proposalCode,
    onSuccess: refresh,
  })

  const onCheckout = useApiErrorHandlingAsyncCallback(async () => {
    switch (paymentType) {
      case 'PAY':
        return isAdmin
          ? await onStripeAdminPayment.execute()
          : await onStripeUserPayment.execute()
      case 'BIND':
        dispatch({
          type: 'UPDATE_PROPOSAL',
          payload: await apiClient.bindProposal(proposal.proposalCode, {
            paymentMethodType: PaymentMethodType.BACS_DEBIT,
          }),
        })
    }
  })

  return (
    <>
      <Section>
        <SectionTitle>{t(tKey('direct-debit', 'title'))}</SectionTitle>
        <SectionDescription>
          {t(
            [
              tKey('direct-debit', 'description', config.type, paymentType),
              tKey('direct-debit', 'description', config.type),
              tKey('direct-debit', 'description'),
            ],
            {
              statement: t(tPaymentMethods('statement')),
            },
          )}
        </SectionDescription>
        <SectionContent>
          <DirectDebitSetup
            customerAccount={customerAccount}
            mandate={usablePaymentMethod}
            context={paymentMethod}
          />
        </SectionContent>
      </Section>

      {['PAY'].includes(paymentType) && (
        <Section>
          <SectionTitle>{t(tKey('deposit', 'title'))}</SectionTitle>
          <SectionDescription>
            {t([
              tKey('deposit', 'description', config.type, paymentType),
              tKey('deposit', 'description', config.type),
              tKey('deposit', 'description'),
            ])}
          </SectionDescription>
        </Section>
      )}

      <PrimaryButton
        data-testid='bind-proposal'
        layout='fixed'
        disabled={!usablePaymentMethod}
        loading={onCheckout.loading}
        onClick={onCheckout.execute}
      >
        {t([
          tKey('action', config.type, paymentType),
          tKey('action', config.type),
          tKey('action'),
        ])}
      </PrimaryButton>
    </>
  )
}

const getPaymentType = (premiums: QuotePremiums): 'PAY' | 'BIND' =>
  premiums.totals.discounted > 0.01 ? 'PAY' : 'BIND'
